/**
 * The small facts the video player reads off a manifest record (KPR-1922).
 *
 * The record's shape is KPR-1921's (keepr-web docs/videos/{key}.md, compiled
 * into the docs portal): key, title, valueLine, format (story | reel |
 * desktop | howto), orientation (v | h), duration (seconds), src, poster,
 * captions (.vtt), transcript (text) and chapters?[] ({ title, at, step? }).
 * Nothing here imports that compiled output: a surface hands the record in.
 */

/** A vertical record plays in the phone-shaped frame; anything else at 16:9. */
export function isVertical(video) {
	return video?.orientation === 'v';
}

const whole = (seconds) => {
	const n = Number(seconds);
	return Number.isFinite(n) && n >= 0 ? Math.floor(n) : null;
};

/** "1:40", "0:45", "1:02:05"; '' for no usable number. */
export function formatDuration(seconds) {
	const total = whole(seconds);
	if (total === null) return '';
	const h = Math.floor(total / 3600);
	const m = Math.floor((total % 3600) / 60);
	const s = String(total % 60).padStart(2, '0');
	return h ? `${h}:${String(m).padStart(2, '0')}:${s}` : `${m}:${s}`;
}

const unit = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** What a screen reader says for a length: "1 minute 40 seconds". */
export function spokenDuration(seconds) {
	const total = whole(seconds);
	if (total === null) return '';
	const h = Math.floor(total / 3600);
	const m = Math.floor((total % 3600) / 60);
	const s = total % 60;
	const parts = [];
	if (h) parts.push(unit(h, 'hour'));
	if (m) parts.push(unit(m, 'minute'));
	if (s || !parts.length) parts.push(unit(s, 'second'));
	return parts.join(' ');
}

/** The chapters worth listing: a title and a time, in time order. */
export function chaptersOf(video) {
	const list = Array.isArray(video?.chapters) ? video.chapters : [];
	return list
		.filter((c) => c && typeof c.title === 'string' && c.title.trim() && whole(c.at) !== null)
		.sort((a, b) => Number(a.at) - Number(b.at));
}

/** The transcript as paragraphs (a blank line separates them). */
export function transcriptParagraphs(text) {
	if (typeof text !== 'string') return [];
	return text.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
}

/**
 * Where a chapter's guide step lives, by default: an anchor on the page the
 * player is on (a guide's Watch sits above its Steps). A surface that shows
 * the player away from the guide passes its own `stepHref`.
 */
export function defaultStepHref(step) {
	return `#step-${encodeURIComponent(String(step))}`;
}
